import { AccountType, RoleOptions } from '@paladin-monorepo/common';
import type { UserProps } from '@/lib/auth';

export type SettingsNavGroup = 'account' | 'profile' | 'billing' | 'security';

export interface SettingsNavItem {
  id: string;
  to: string;
  labelKey: string;
  descriptionKey: string;
  group: SettingsNavGroup;
  isVisible: (user: UserProps | null | undefined) => boolean;
}

export const SETTINGS_NAV_GROUP_ORDER: SettingsNavGroup[] = [
  'account',
  'profile',
  'billing',
  'security',
];

export const SETTINGS_NAV_GROUP_LABEL_KEYS: Record<SettingsNavGroup, string> = {
  account: 'settings.overview.groups.account',
  profile: 'settings.overview.groups.profile',
  billing: 'settings.overview.groups.billing',
  security: 'settings.overview.groups.security',
};

const isAdmin = (user: UserProps | null | undefined) =>
  user?.role === RoleOptions.ACCOUNT_OWNER ||
  user?.role === RoleOptions.ACCOUNT_ADMIN;

const isSupplierAccount = (user: UserProps | null | undefined) =>
  user?.account?.accountType === AccountType.Consultant ||
  user?.account?.accountType === AccountType.SolutionProvider;

export const settingsNavItems: SettingsNavItem[] = [
  {
    id: 'general',
    to: '/settings/general',
    labelKey: 'settings.tabs.general',
    descriptionKey: 'settings.overview.descriptions.general',
    group: 'account',
    isVisible: isAdmin,
  },
  {
    id: 'users',
    to: '/settings/users',
    labelKey: 'settings.tabs.users',
    descriptionKey: 'settings.overview.descriptions.users',
    group: 'account',
    isVisible: isAdmin,
  },
  {
    id: 'signers',
    to: '/settings/signers',
    labelKey: 'settings.tabs.signers',
    descriptionKey: 'settings.overview.descriptions.signers',
    group: 'account',
    isVisible: isAdmin,
  },
  {
    id: 'cenotvorba',
    to: '/settings/cenotvorba',
    labelKey: 'settings.tabs.cenotvorba',
    descriptionKey: 'settings.overview.descriptions.cenotvorba',
    group: 'account',
    isVisible: (user) => isAdmin(user) && isSupplierAccount(user),
  },
  {
    id: 'profile',
    to: '/settings/profile',
    labelKey: 'settings.tabs.profile',
    descriptionKey: 'settings.overview.descriptions.profile',
    group: 'profile',
    isVisible: () => true,
  },
  {
    id: 'payouts',
    to: '/settings/payouts',
    labelKey: 'settings.tabs.payouts',
    descriptionKey: 'settings.overview.descriptions.payouts',
    group: 'billing',
    isVisible: isSupplierAccount,
  },
  {
    id: 'billing',
    to: '/settings/billing',
    labelKey: 'settings.tabs.billing',
    descriptionKey: 'settings.overview.descriptions.billing',
    group: 'billing',
    isVisible: isAdmin,
  },
  {
    id: 'subscription',
    to: '/settings/subscription',
    labelKey: 'settings.tabs.subscription',
    descriptionKey: 'settings.overview.descriptions.subscription',
    group: 'billing',
    isVisible: isAdmin,
  },
  {
    id: 'password',
    to: '/settings/password',
    labelKey: 'settings.tabs.password',
    descriptionKey: 'settings.overview.descriptions.password',
    group: 'security',
    isVisible: () => true,
  },
];

export function getVisibleSettingsNavItems(
  user: UserProps | null | undefined,
): SettingsNavItem[] {
  return settingsNavItems.filter((item) => item.isVisible(user));
}

export function getSettingsNavItemForPath(pathname: string): SettingsNavItem | undefined {
  return settingsNavItems.find(
    (item) => pathname === item.to || pathname.startsWith(`${item.to}/`),
  );
}
